import { Injectable } from '@angular/core';

import type { FrontendProduct, FrontendStore } from './api.models';

/**
 * Hands a page the payload the root-slug resolver already fetched for it.
 *
 * A bare one-segment URL (`/zulily-coupon-codes`, `/1-oz-silver-maples-monster-box-500-pc`) can be a
 * store or a product, and only the server can tell which — see `GET /api/frontend/resolve/:slug`.
 * That call already returns the full payload, so the page it hands off to must not go and fetch the
 * same thing again.
 *
 * The page components are left untouched: they still call `getStore()` / `getProduct()` as they do
 * on their own routes, and the API service checks here first. That keeps one code path for a page
 * whether it was reached at the root or at its own URL.
 *
 * Entries are SINGLE USE. A stale entry would serve the previous visit's data after an edit, so a
 * payload is handed over exactly once and the slot cleared — the next navigation to the same URL
 * fetches fresh. Only one slug is ever held: the resolver fills it immediately before the page reads
 * it, so there is nothing to accumulate.
 */
@Injectable({ providedIn: 'root' })
export class SlugPrefetchStore {
  private storeSlug = '';
  private store: FrontendStore | null = null;
  private productSlug = '';
  private product: FrontendProduct | null = null;

  setStore(slug: string, store: FrontendStore): void {
    this.storeSlug = slug;
    this.store = store;
  }

  setProduct(slug: string, product: FrontendProduct): void {
    this.productSlug = slug;
    this.product = product;
  }

  /** The prefetched store for this slug, or null. Clears the slot. */
  takeStore(slug: string): FrontendStore | null {
    if (!this.store || this.storeSlug !== slug) return null;
    const store = this.store;
    this.storeSlug = '';
    this.store = null;
    return store;
  }

  /** The prefetched product for this slug, or null. Clears the slot. */
  takeProduct(slug: string): FrontendProduct | null {
    if (!this.product || this.productSlug !== slug) return null;
    const product = this.product;
    this.productSlug = '';
    this.product = null;
    return product;
  }

  clear(): void {
    this.storeSlug = '';
    this.store = null;
    this.productSlug = '';
    this.product = null;
  }
}
